import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent,
  type WheelEvent,
} from "react";
import {
  edgeName,
  fmtFt,
  getStairGeometry,
  normalizeRailedEdges,
  snapPt,
  type StairsConfig,
  type Vertex,
} from "../lib/geometry";
import type { Design } from "../lib/types";
import type { RailColor } from "../three/buildDeck";

type Props = {
  vertices: Vertex[];
  stairs: StairsConfig;
  railedEdges: boolean[];
  onDesignChange: (
    patch: Pick<Partial<Design>, "vertices" | "railedEdges">,
  ) => void;
  zoom: number;
  onZoomChange: (z: number) => void;
  color: RailColor;
  pulseKey?: number;
};

// Hex matches the swatches in Designer's COLORS list and the 3D material
// colors in buildDeck.ts so the 2D and 3D views agree.
const RAIL_HEX: Record<RailColor, string> = {
  black: "#1a1a1a",
  silver: "#9aa1a7",
  white: "#ececec",
};

type Drag = {
  pointerId: number;
  index: number;
  moved: boolean;
  startClientX: number;
  startClientY: number;
};

type Pan = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startCenterX: number;
  startCenterY: number;
};

type EdgeTap = {
  pointerId: number;
  edgeIdx: number;
  startClientX: number;
  startClientY: number;
};

type Pinch = {
  startDist: number;
  startZoom: number;
  midClientX: number;
  midClientY: number;
};

const DRAG_THRESHOLD_PX = 4;
const BASE_HALF_HEIGHT_FT = 22; // visible half-height at zoom = 1
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 8;
// Inserted posts must sit at least this far from the existing posts on
// either end of the edge they're inserted into.
const MIN_POST_GAP_FT = 2;
// Must match POST_SPACING_FT in src/three/buildDeck.ts so the 2D and 3D
// views agree on post layout.
const POST_SPACING_FT = 4;

export default function DeckDiagram({
  vertices,
  stairs,
  railedEdges,
  onDesignChange,
  zoom,
  onZoomChange,
  color,
}: Props) {
  const railHex = RAIL_HEX[color];
  const svgRef = useRef<SVGSVGElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const panRef = useRef<Pan | null>(null);
  const edgeTapRef = useRef<EdgeTap | null>(null);
  const activePointersRef = useRef<Map<number, { x: number; y: number }>>(
    new Map(),
  );
  const pinchRef = useRef<Pinch | null>(null);
  const sawPinchRef = useRef(false);

  const [center, setCenter] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [panning, setPanning] = useState(false);
  const [edgeMenu, setEdgeMenu] = useState<{
    edgeIdx: number;
    clientX: number;
    clientY: number;
  } | null>(null);
  const [edgeHover, setEdgeHover] = useState<{
    edgeIdx: number;
    point: Vertex;
    fromStart: number;
    edgeLen: number;
  } | null>(null);

  const railed = useMemo(
    () => normalizeRailedEdges(railedEdges, vertices.length),
    [railedEdges, vertices.length],
  );

  // Track container size for aspect-correct viewBox.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const ro = new ResizeObserver(([entry]) => {
      const r = entry.contentRect;
      if (r.width > 0 && r.height > 0) setSize({ w: r.width, h: r.height });
    });
    ro.observe(svg);
    return () => ro.disconnect();
  }, []);

  const halfH = BASE_HALF_HEIGHT_FT / zoom;
  const aspect = size.w / size.h;
  const halfW = halfH * aspect;
  const viewMinX = center.x - halfW;
  const viewMaxX = center.x + halfW;
  const viewMinY = center.y - halfH;
  const viewMaxY = center.y + halfH;
  // viewBox uses inverted Y (SVG y-down vs CAD y-up).
  const viewBox = `${viewMinX} ${-viewMaxY} ${halfW * 2} ${halfH * 2}`;
  const unitsPerPx = (halfH * 2) / Math.max(size.h, 1);
  // pixel → CAD ft converter for size-stable elements.
  const px = (n: number) => n * unitsPerPx;

  const stairsG = getStairGeometry(vertices, stairs);

  const gridStep = 1;
  const majorEvery = 5;
  const gridLinesV = useMemo(() => {
    const start = Math.floor(viewMinX / gridStep) * gridStep;
    const end = Math.ceil(viewMaxX / gridStep) * gridStep;
    const out: { x: number; major: boolean }[] = [];
    for (let g = start; g <= end; g += gridStep) {
      out.push({ x: g, major: g % majorEvery === 0 });
    }
    return out;
  }, [viewMinX, viewMaxX]);
  const gridLinesH = useMemo(() => {
    const start = Math.floor(viewMinY / gridStep) * gridStep;
    const end = Math.ceil(viewMaxY / gridStep) * gridStep;
    const out: { y: number; major: boolean }[] = [];
    for (let g = start; g <= end; g += gridStep) {
      out.push({ y: g, major: g % majorEvery === 0 });
    }
    return out;
  }, [viewMinY, viewMaxY]);

  // Coordinate transforms
  function clientToCAD(clientX: number, clientY: number): Vertex {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const fracX = (clientX - rect.left) / rect.width;
    const fracY = (clientY - rect.top) / rect.height;
    return {
      x: viewMinX + fracX * halfW * 2,
      y: viewMaxY - fracY * halfH * 2,
    };
  }

  // Vertex drag
  function onVertexPointerDown(idx: number) {
    return (e: PointerEvent<SVGElement>) => {
      e.stopPropagation();
      e.preventDefault();
      (e.target as SVGElement).setPointerCapture(e.pointerId);
      dragRef.current = {
        pointerId: e.pointerId,
        index: idx,
        moved: false,
        startClientX: e.clientX,
        startClientY: e.clientY,
      };
    };
  }

  function onVertexPointerMove(e: PointerEvent<SVGElement>) {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startClientX;
    const dy = e.clientY - d.startClientY;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    d.moved = true;
    const cad = snapPt(clientToCAD(e.clientX, e.clientY));
    const next = vertices.slice();
    next[d.index] = cad;
    onDesignChange({ vertices: next });
  }

  function onVertexPointerUp(e: PointerEvent<SVGElement>) {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    try {
      (e.target as SVGElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    dragRef.current = null;
  }

  // Edge insertion — hover shows a snapped preview, click commits.
  // Project the cursor onto edge `edgeIdx`, clamp to MIN_POST_GAP_FT from
  // either endpoint, snap to 0.5 ft. Returns null if the edge is too short
  // to fit a new post within the min-gap rule.
  function projectOntoEdge(
    edgeIdx: number,
    clientX: number,
    clientY: number,
  ): {
    edgeIdx: number;
    point: Vertex;
    fromStart: number;
    edgeLen: number;
  } | null {
    const a = vertices[edgeIdx];
    const b = vertices[(edgeIdx + 1) % vertices.length];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const len = Math.sqrt(len2);
    if (len < MIN_POST_GAP_FT * 2) return null;
    const cad = clientToCAD(clientX, clientY);
    const t = ((cad.x - a.x) * dx + (cad.y - a.y) * dy) / len2;
    let dist = t * len;
    dist = Math.max(MIN_POST_GAP_FT, Math.min(len - MIN_POST_GAP_FT, dist));
    dist = Math.round(dist * 2) / 2;
    dist = Math.max(MIN_POST_GAP_FT, Math.min(len - MIN_POST_GAP_FT, dist));
    const ux = dx / len;
    const uy = dy / len;
    return {
      edgeIdx,
      point: { x: a.x + ux * dist, y: a.y + uy * dist },
      fromStart: dist,
      edgeLen: len,
    };
  }

  function onEdgePointerDown(edgeIdx: number) {
    return (e: PointerEvent<SVGElement>) => {
      // Right/middle/back/forward buttons never trigger insert; they fall
      // through to the contextmenu handler (right-click → toggle railing).
      if (e.pointerType === "mouse" && e.button !== 0) return;
      // If a second finger is already down, skip — this is a pinch.
      if (activePointersRef.current.size >= 2) return;
      e.stopPropagation();
      e.preventDefault();
      (e.target as SVGElement).setPointerCapture(e.pointerId);
      edgeTapRef.current = {
        pointerId: e.pointerId,
        edgeIdx,
        startClientX: e.clientX,
        startClientY: e.clientY,
      };
    };
  }

  function onEdgePointerMove(edgeIdx: number) {
    return (e: PointerEvent<SVGElement>) => {
      // Drag-detection for the in-flight tap (so a click+drag pans, not inserts).
      const t = edgeTapRef.current;
      if (t && t.pointerId === e.pointerId) {
        const dx = e.clientX - t.startClientX;
        const dy = e.clientY - t.startClientY;
        if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
          edgeTapRef.current = null;
          setEdgeHover(null);
          return;
        }
      }
      // Hover preview — mouse/pen only. Touch does not show a hover dot
      // because there's no hover state without a button down.
      if (e.pointerType === "touch") return;
      setEdgeHover(projectOntoEdge(edgeIdx, e.clientX, e.clientY));
    };
  }

  function onEdgePointerUp(edgeIdx: number) {
    return (e: PointerEvent<SVGElement>) => {
      const t = edgeTapRef.current;
      if (!t || t.pointerId !== e.pointerId) return;
      edgeTapRef.current = null;
      try {
        (e.target as SVGElement).releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (sawPinchRef.current) return;
      const h = projectOntoEdge(edgeIdx, e.clientX, e.clientY);
      if (!h) return;
      const next = vertices.slice();
      next.splice(t.edgeIdx + 1, 0, h.point);
      // Inserted vertex splits edge t.edgeIdx into two edges. Both inherit the
      // original edge's railing state so the kit/visual stay consistent.
      const inheritedRailed = railed[t.edgeIdx] ?? true;
      const nextRailed = [
        ...railed.slice(0, t.edgeIdx),
        inheritedRailed,
        inheritedRailed,
        ...railed.slice(t.edgeIdx + 1),
      ];
      onDesignChange({ vertices: next, railedEdges: nextRailed });
      setEdgeHover(null);
    };
  }

  function onEdgePointerLeave() {
    setEdgeHover(null);
  }

  function onEdgeContextMenu(edgeIdx: number) {
    return (e: ReactMouseEvent<SVGElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setEdgeMenu({ edgeIdx, clientX: e.clientX, clientY: e.clientY });
    };
  }

  function toggleEdgeRailing(edgeIdx: number) {
    const next = railed.slice();
    next[edgeIdx] = !next[edgeIdx];
    onDesignChange({ railedEdges: next });
  }

  useEffect(() => {
    if (!edgeMenu) return;
    const onDoc = (e: globalThis.MouseEvent) => {
      const wrap = wrapRef.current;
      if (wrap && !wrap.contains(e.target as Node)) setEdgeMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setEdgeMenu(null);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [edgeMenu]);

  // Background pan
  function onBgPointerDown(e: PointerEvent<SVGElement>) {
    e.preventDefault();
    (e.target as SVGElement).setPointerCapture(e.pointerId);
    panRef.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startCenterX: center.x,
      startCenterY: center.y,
    };
    setPanning(true);
  }

  function onBgPointerMove(e: PointerEvent<SVGElement>) {
    const p = panRef.current;
    if (!p || p.pointerId !== e.pointerId) return;
    const dx = e.clientX - p.startClientX;
    const dy = e.clientY - p.startClientY;
    const ftPerPx = (halfH * 2) / Math.max(size.h, 1);
    setCenter({
      x: p.startCenterX - dx * ftPerPx,
      y: p.startCenterY + dy * ftPerPx,
    });
  }

  function onBgPointerUp(e: PointerEvent<SVGElement>) {
    const p = panRef.current;
    if (!p || p.pointerId !== e.pointerId) return;
    try {
      (e.target as SVGElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    panRef.current = null;
    setPanning(false);
  }

  function applyZoomAround(newZoom: number, anchorClientX: number, anchorClientY: number) {
    const cad = clientToCAD(anchorClientX, anchorClientY);
    const newHalfH = BASE_HALF_HEIGHT_FT / newZoom;
    const newHalfW = newHalfH * aspect;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const fracX = (anchorClientX - rect.left) / rect.width;
    const fracY = (anchorClientY - rect.top) / rect.height;
    setCenter({
      x: cad.x - newHalfW * (2 * fracX - 1),
      y: cad.y + newHalfH * (2 * fracY - 1),
    });
    onZoomChange(newZoom);
  }

  // Capture-phase pointer tracking on the SVG so we always see *every* pointer,
  // even when a child element captured one (vertex drag, edge tap, bg pan).
  function onSvgPointerDownCapture(e: PointerEvent<SVGSVGElement>) {
    activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointersRef.current.size >= 2 && !pinchRef.current) {
      const [p1, p2] = [...activePointersRef.current.values()];
      const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      pinchRef.current = {
        startDist: Math.max(dist, 1),
        startZoom: zoom,
        midClientX: (p1.x + p2.x) / 2,
        midClientY: (p1.y + p2.y) / 2,
      };
      sawPinchRef.current = true;
      // Cancel any in-flight gestures so they don't keep firing or commit.
      panRef.current = null;
      setPanning(false);
      dragRef.current = null;
      edgeTapRef.current = null;
    }
  }

  function onSvgPointerMoveCapture(e: PointerEvent<SVGSVGElement>) {
    const ap = activePointersRef.current;
    if (!ap.has(e.pointerId)) return;
    ap.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const ps = pinchRef.current;
    if (ap.size >= 2 && ps) {
      const [p1, p2] = [...ap.values()];
      const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      if (dist <= 0) return;
      const factor = dist / ps.startDist;
      const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, ps.startZoom * factor));
      applyZoomAround(newZoom, ps.midClientX, ps.midClientY);
    }
  }

  function onSvgPointerUpCapture(e: PointerEvent<SVGSVGElement>) {
    activePointersRef.current.delete(e.pointerId);
    if (activePointersRef.current.size < 2) {
      pinchRef.current = null;
    }
    if (activePointersRef.current.size === 0) {
      sawPinchRef.current = false;
    }
  }

  // Wheel zoom (around cursor)
  function onWheel(e: WheelEvent<SVGSVGElement>) {
    e.preventDefault();
    const factor = Math.exp(-e.deltaY * 0.0015);
    const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom * factor));
    if (newZoom === zoom) return;
    applyZoomAround(newZoom, e.clientX, e.clientY);
  }

  // Stair geometry pieces. `startIsVertex`/`endIsVertex` track whether the
  // segment endpoint coincides with a polygon vertex (so we skip drawing a
  // duplicate post-dot there) or with a stair gap (no vertex, draw a post).
  type Segment = {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    startIsVertex: boolean;
    endIsVertex: boolean;
  };
  const edges: { i: number; segments: Segment[] }[] = [];
  for (let i = 0; i < vertices.length; i++) {
    const a = vertices[i];
    const b = vertices[(i + 1) % vertices.length];
    let segments: Segment[] = [
      { x1: a.x, y1: a.y, x2: b.x, y2: b.y, startIsVertex: true, endIsVertex: true },
    ];
    if (
      stairsG &&
      stairs.withRailing &&
      stairsG.edgeIndex === i &&
      stairsG.stairWidth + 0.05 < stairsG.edgeLen
    ) {
      const halfWStair = stairsG.stairWidth / 2;
      const gapL = {
        x: stairsG.midX - stairsG.tx * halfWStair,
        y: stairsG.midY - stairsG.ty * halfWStair,
      };
      const gapR = {
        x: stairsG.midX + stairsG.tx * halfWStair,
        y: stairsG.midY + stairsG.ty * halfWStair,
      };
      segments = [
        { x1: a.x, y1: a.y, x2: gapL.x, y2: gapL.y, startIsVertex: true, endIsVertex: false },
        { x1: gapR.x, y1: gapR.y, x2: b.x, y2: b.y, startIsVertex: false, endIsVertex: true },
      ];
    }
    edges.push({ i, segments });
  }

  // Intermediate / gap-end posts mirroring buildDeck's layout. Vertex corners
  // are still drawn as draggable post-dots below; we skip those endpoints here.
  type IntermediatePost = { key: string; x: number; y: number };
  const intermediatePosts: IntermediatePost[] = [];
  for (const { i, segments } of edges) {
    if (!railed[i]) continue;
    segments.forEach((seg, segIdx) => {
      const dx = seg.x2 - seg.x1;
      const dy = seg.y2 - seg.y1;
      const segLen = Math.hypot(dx, dy);
      if (segLen < 0.05) return;
      const nPosts = Math.max(2, Math.ceil(segLen / POST_SPACING_FT) + 1);
      for (let j = 0; j < nPosts; j++) {
        if (j === 0 && seg.startIsVertex) continue;
        if (j === nPosts - 1 && seg.endIsVertex) continue;
        const t = j / (nPosts - 1);
        intermediatePosts.push({
          key: `post-${i}-${segIdx}-${j}`,
          x: seg.x1 + dx * t,
          y: seg.y1 + dy * t,
        });
      }
    });
  }

  let stairCorners: { c1: Vertex; c2: Vertex; c3: Vertex; c4: Vertex; labelPt: Vertex } | null = null;
  if (stairsG) {
    const half = stairsG.stairWidth / 2;
    const taper = Math.min(half * 0.18, 0.4);
    const depth = Math.min(stairsG.stairLength, 14);
    stairCorners = {
      c1: {
        x: stairsG.midX - stairsG.tx * half,
        y: stairsG.midY - stairsG.ty * half,
      },
      c2: {
        x: stairsG.midX + stairsG.tx * half,
        y: stairsG.midY + stairsG.ty * half,
      },
      c3: {
        x: stairsG.midX + stairsG.tx * (half - taper) + stairsG.ox * depth,
        y: stairsG.midY + stairsG.ty * (half - taper) + stairsG.oy * depth,
      },
      c4: {
        x: stairsG.midX - stairsG.tx * (half - taper) + stairsG.ox * depth,
        y: stairsG.midY - stairsG.ty * (half - taper) + stairsG.oy * depth,
      },
      labelPt: {
        x: stairsG.midX + stairsG.ox * (depth + 1.5),
        y: stairsG.midY + stairsG.oy * (depth + 1.5),
      },
    };
  }

  const polyPoints = vertices.map((p) => `${p.x},${-p.y}`).join(" ");

  // Subtle deck-board gap lines spaced one board-width apart, running along
  // the longer bbox dimension (boards typically run the longest length).
  // Lines are drawn across the bbox and clipped to the deck polygon.
  const BOARD_WIDTH_FT = 6 / 12;
  const boardLines = useMemo(() => {
    if (vertices.length < 3) return [] as { x1: number; y1: number; x2: number; y2: number }[];
    const xs = vertices.map((v) => v.x);
    const ys = vertices.map((v) => v.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const horizontal = maxX - minX >= maxY - minY;
    const out: { x1: number; y1: number; x2: number; y2: number }[] = [];
    if (horizontal) {
      const start = Math.ceil(minY / BOARD_WIDTH_FT) * BOARD_WIDTH_FT;
      for (let y = start; y < maxY; y += BOARD_WIDTH_FT) {
        out.push({ x1: minX, y1: y, x2: maxX, y2: y });
      }
    } else {
      const start = Math.ceil(minX / BOARD_WIDTH_FT) * BOARD_WIDTH_FT;
      for (let x = start; x < maxX; x += BOARD_WIDTH_FT) {
        out.push({ x1: x, y1: minY, x2: x, y2: maxY });
      }
    }
    return out;
  }, [vertices]);

  // Sized for legibility — pixel-stable.
  const fontSize = px(11);
  const hitR = px(18);
  // Posts and top-rail are both 2" square in real life, so the 2D view draws
  // them at the same width. Real CAD scale, with a pixel floor so they
  // remain visible at low zoom.
  const POST_SIZE_FT = 2 / 12;
  const railPostW = Math.max(POST_SIZE_FT, px(7));
  const dotR = railPostW / 2;

  const svgEl = (
    <svg
      ref={svgRef}
      className={`deck-diagram${panning ? " panning" : ""}`}
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      onWheel={onWheel}
      onPointerDownCapture={onSvgPointerDownCapture}
      onPointerMoveCapture={onSvgPointerMoveCapture}
      onPointerUpCapture={onSvgPointerUpCapture}
      onPointerCancelCapture={onSvgPointerUpCapture}
    >
      {/* Background pan target */}
      <rect
        x={viewMinX}
        y={-viewMaxY}
        width={halfW * 2}
        height={halfH * 2}
        className="canvas-bg"
        onPointerDown={onBgPointerDown}
        onPointerMove={onBgPointerMove}
        onPointerUp={onBgPointerUp}
        onPointerCancel={onBgPointerUp}
      />

      {/* Grid */}
      {gridLinesV.map((l) => (
        <line
          key={`v${l.x}`}
          className={`diagram-grid${l.major ? " major" : ""}`}
          x1={l.x}
          y1={-viewMaxY}
          x2={l.x}
          y2={-viewMinY}
        />
      ))}
      {gridLinesH.map((l) => (
        <line
          key={`h${l.y}`}
          className={`diagram-grid${l.major ? " major" : ""}`}
          x1={viewMinX}
          y1={-l.y}
          x2={viewMaxX}
          y2={-l.y}
        />
      ))}

      <defs>
        <clipPath id="deck-clip">
          <polygon points={polyPoints} />
        </clipPath>
      </defs>

      <polygon points={polyPoints} className="deck-fill" />
      <g className="deck-boards" clipPath="url(#deck-clip)">
        {boardLines.map((l, i) => (
          <line
            key={`board-${i}`}
            x1={l.x1}
            y1={-l.y1}
            x2={l.x2}
            y2={-l.y2}
          />
        ))}
      </g>
      {(() => {
        if (vertices.length < 3) return null;
        const xs = vertices.map((v) => v.x);
        const ys = vertices.map((v) => v.y);
        const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
        const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
        const span = Math.min(
          Math.max(...xs) - Math.min(...xs),
          Math.max(...ys) - Math.min(...ys),
        );
        const w = span * 0.6;
        const h = w / (1189 / 255); // icon aspect ≈ 1189:255
        return (
          <image
            href="/viewrail-icon.png"
            className="deck-watermark"
            x={cx - w / 2}
            y={-cy - h / 2}
            width={w}
            height={h}
            clipPath="url(#deck-clip)"
            preserveAspectRatio="xMidYMid meet"
          />
        );
      })()}

      {stairCorners && stairsG && (
        <g>
          <polygon
            points={`${stairCorners.c1.x},${-stairCorners.c1.y} ${stairCorners.c2.x},${-stairCorners.c2.y} ${stairCorners.c3.x},${-stairCorners.c3.y} ${stairCorners.c4.x},${-stairCorners.c4.y}`}
            className="deck-fill"
          />
          {[1, 2, 3].map((k) => {
            const t = k / 4;
            const lx = stairCorners!.c1.x + (stairCorners!.c4.x - stairCorners!.c1.x) * t;
            const ly = stairCorners!.c1.y + (stairCorners!.c4.y - stairCorners!.c1.y) * t;
            const rx = stairCorners!.c2.x + (stairCorners!.c3.x - stairCorners!.c2.x) * t;
            const ry = stairCorners!.c2.y + (stairCorners!.c3.y - stairCorners!.c2.y) * t;
            return (
              <line
                key={`step-${k}`}
                x1={lx}
                y1={-ly}
                x2={rx}
                y2={-ry}
                stroke="#a89870"
                strokeWidth={1}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
          {stairs.withRailing && (
            <>
              <line
                className="side on"
                x1={stairCorners.c1.x}
                y1={-stairCorners.c1.y}
                x2={stairCorners.c4.x}
                y2={-stairCorners.c4.y}
                style={{ strokeWidth: railPostW, vectorEffect: "none", stroke: railHex }}
              />
              <line
                className="side on"
                x1={stairCorners.c2.x}
                y1={-stairCorners.c2.y}
                x2={stairCorners.c3.x}
                y2={-stairCorners.c3.y}
                style={{ strokeWidth: railPostW, vectorEffect: "none", stroke: railHex }}
              />
            </>
          )}
          <text
            className="dim-label"
            x={stairCorners.labelPt.x}
            y={-stairCorners.labelPt.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={fontSize}
          >
            {`Stairs — ${fmtFt(stairsG.stairLength)}`}
          </text>
        </g>
      )}

      {edges.map(({ i, segments }) =>
        segments.map((seg, segIdx) => (
          <g key={`edge-${i}-${segIdx}`}>
            <line
              className={railed[i] ? "side on" : "side off"}
              x1={seg.x1}
              y1={-seg.y1}
              x2={seg.x2}
              y2={-seg.y2}
              style={
                railed[i]
                  ? { strokeWidth: railPostW, vectorEffect: "none", stroke: railHex }
                  : undefined
              }
            />
            <line
              className="side-hit"
              x1={seg.x1}
              y1={-seg.y1}
              x2={seg.x2}
              y2={-seg.y2}
              strokeWidth={px(28)}
              onPointerDown={onEdgePointerDown(i)}
              onPointerMove={onEdgePointerMove(i)}
              onPointerUp={onEdgePointerUp(i)}
              onPointerCancel={onEdgePointerUp(i)}
              onPointerLeave={onEdgePointerLeave}
              onContextMenu={onEdgeContextMenu(i)}
            />
          </g>
        )),
      )}

      {vertices.map((a, i) => {
        const b = vertices[(i + 1) % vertices.length];
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const len = Math.hypot(dx, dy);
        if (len < 0.5) return null;
        const outX = dy / len;
        const outY = -dx / len;
        const off = px(14);
        return (
          <text
            key={`label${i}`}
            className="dim-label"
            x={mx + outX * off}
            y={-(my + outY * off)}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={fontSize}
          >
            {`${edgeName(vertices, i)} — ${fmtFt(len)}${railed[i] ? "" : " · no rail"}`}
          </text>
        );
      })}

      {intermediatePosts.map((p) => (
        <rect
          key={p.key}
          x={p.x - dotR}
          y={-p.y - dotR}
          width={dotR * 2}
          height={dotR * 2}
          className="post-dot"
          style={{ fill: railHex }}
        />
      ))}

      {vertices.map((p, i) => (
        <g key={`vertex-${i}`}>
          <rect
            x={p.x - hitR}
            y={-p.y - hitR}
            width={hitR * 2}
            height={hitR * 2}
            className="post-dot-hit"
            onPointerDown={onVertexPointerDown(i)}
            onPointerMove={onVertexPointerMove}
            onPointerUp={onVertexPointerUp}
            onPointerCancel={onVertexPointerUp}
          />
          <rect
            x={p.x - dotR}
            y={-p.y - dotR}
            width={dotR * 2}
            height={dotR * 2}
            className="post-dot"
            style={{ fill: railHex }}
          />
        </g>
      ))}

      {edgeHover && (() => {
        const a = vertices[edgeHover.edgeIdx];
        const b = vertices[(edgeHover.edgeIdx + 1) % vertices.length];
        const ux = (b.x - a.x) / edgeHover.edgeLen;
        const uy = (b.y - a.y) / edgeHover.edgeLen;
        // Perpendicular outward normal (same side that edge labels use).
        const nx = uy;
        const ny = -ux;
        const labOff = px(14);
        const leftMid = {
          x: a.x + ux * (edgeHover.fromStart / 2),
          y: a.y + uy * (edgeHover.fromStart / 2),
        };
        const rightStart = edgeHover.fromStart;
        const rightLen = edgeHover.edgeLen - rightStart;
        const rightMid = {
          x: a.x + ux * (rightStart + rightLen / 2),
          y: a.y + uy * (rightStart + rightLen / 2),
        };
        return (
          <g pointerEvents="none">
            <rect
              x={edgeHover.point.x - dotR}
              y={-edgeHover.point.y - dotR}
              width={dotR * 2}
              height={dotR * 2}
              className="post-dot preview"
              style={{ fill: railHex }}
            />
            <text
              className="dim-label preview"
              x={leftMid.x + nx * labOff}
              y={-(leftMid.y + ny * labOff)}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={fontSize}
            >
              {fmtFt(edgeHover.fromStart)}
            </text>
            <text
              className="dim-label preview"
              x={rightMid.x + nx * labOff}
              y={-(rightMid.y + ny * labOff)}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={fontSize}
            >
              {fmtFt(rightLen)}
            </text>
          </g>
        );
      })()}
    </svg>
  );

  const menuRect = edgeMenu ? wrapRef.current?.getBoundingClientRect() : null;
  const menuX = edgeMenu && menuRect
    ? Math.min(edgeMenu.clientX - menuRect.left, menuRect.width - 200)
    : 0;
  const menuY = edgeMenu && menuRect
    ? Math.min(edgeMenu.clientY - menuRect.top, menuRect.height - 56)
    : 0;

  return (
    <div ref={wrapRef} className="relative h-full w-full">
      {svgEl}
      {edgeMenu && menuRect && (
        <div
          role="menu"
          className="absolute z-30 min-w-[12rem] overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.18)]"
          style={{ left: Math.max(menuX, 0), top: Math.max(menuY, 0) }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              toggleEdgeRailing(edgeMenu.edgeIdx);
              setEdgeMenu(null);
            }}
            className="block w-full px-3 py-2 text-left text-sm text-neutral-800 transition hover:bg-neutral-100"
          >
            {railed[edgeMenu.edgeIdx]
              ? "No railing on this side"
              : "Restore railing on this side"}
          </button>
        </div>
      )}
    </div>
  );
}
